import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../lib/AuthContext";

/**
 * AppShell — Layout für authentifizierte App-Bereiche unter /app/*.
 *
 * Struktur:
 *   ┌──────────────────────────────────────────────────────┐
 *   │  Top-Nav (Logo, Email, Logout)                       │
 *   ├──────────┬───────────────────────────────────────────┤
 *   │  Sidebar │  Outlet (current page)                    │
 *   │  - Home  │                                           │
 *   │  - Quick │                                           │
 *   │  - Case  │                                           │
 *   │  - Letter│                                           │
 *   │  - Tax   │                                           │
 *   │  - Hist. │                                           │
 *   │  - Aff.  │                                           │
 *   └──────────┴───────────────────────────────────────────┘
 *
 * Mobile (<lg): Sidebar wird Burger-Menu im Top-Nav.
 */
export default function AppShell() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Auto-close burger nach Route-Wechsel auf Mobile
  const handleNav = () => setMobileOpen(false);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top-Nav */}
      <header className="border-b border-slate-800/60 backdrop-blur-md sticky top-0 z-40 bg-slate-950/85">
        <div className="px-4 lg:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden rounded-md border border-slate-700 hover:border-slate-500 p-2 transition"
              aria-label="Menu"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M3 12h18M3 18h18" />
              </svg>
            </button>
            <Link to="/app" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-court-400 to-court-700 flex items-center justify-center font-bold">
                ⚖
              </div>
              <span className="font-semibold tracking-tight hidden sm:inline">Shadow Court</span>
            </Link>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-300">
            {user && (
              <>
                <span className="text-xs text-slate-500 font-mono hidden md:inline">
                  {user.email}
                </span>
                <button
                  onClick={logout}
                  className="rounded border border-slate-700 hover:border-slate-500 px-3 py-1 text-xs transition"
                >
                  Logout
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Body: Sidebar + Content */}
      <div className="flex-1 flex">
        {/* Sidebar — fixed auf desktop, drawer auf mobile */}
        <aside
          className={`
            ${mobileOpen ? "block" : "hidden"}
            lg:block lg:w-64 lg:flex-shrink-0
            fixed lg:static inset-y-16 left-0 z-30
            w-64 lg:w-64
            bg-slate-950 lg:bg-transparent
            border-r border-slate-800/60
            overflow-y-auto
          `}
        >
          <nav className="p-4 space-y-1" onClick={handleNav}>
            <SidebarSection label="Werkzeuge">
              <SidebarLink to="/app" end icon="🏠" label="Übersicht" />
              <SidebarLink to="/app/quick" icon="⚡" label="Schnellfrage" />
              <SidebarLink to="/app/case" icon="⚖" label="Fall analysieren" />
              <SidebarLink to="/app/letter" icon="✉️" label="Brief entwerfen" />
              <SidebarLink to="/app/structuring" icon="🏛" label="Steuer-Strukturierung" />
            </SidebarSection>

            <SidebarSection label="Konto">
              <SidebarLink to="/app/history" icon="📚" label="Meine Historie" />
              <SidebarLink to="/app/affiliate" icon="💸" label="Affiliate" />
            </SidebarSection>

            <SidebarSection label="Rechtliches">
              <SidebarLinkExtern to="/impressum" label="Impressum" />
              <SidebarLinkExtern to="/datenschutz" label="Datenschutz" />
              <SidebarLinkExtern to="/agb" label="AGB" />
            </SidebarSection>
          </nav>
        </aside>

        {/* Mobile-Overlay hinter dem Drawer */}
        {mobileOpen && (
          <div
            className="lg:hidden fixed inset-0 top-16 z-20 bg-slate-950/60"
            onClick={() => setMobileOpen(false)}
          />
        )}

        {/* Main */}
        <main className="flex-1 min-w-0">
          <div
            key={location.pathname}
            className="p-4 lg:p-8 max-w-5xl mx-auto"
          >
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function SidebarSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="pt-4 first:pt-0">
      <h3 className="px-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
        {label}
      </h3>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function SidebarLink({
  to,
  icon,
  label,
  end,
}: {
  to: string;
  icon: string;
  label: string;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition ${
          isActive
            ? "bg-court-900/60 border border-court-700/60 text-white"
            : "text-slate-300 hover:bg-slate-900/60 hover:text-white"
        }`
      }
    >
      <span className="text-base w-5 text-center">{icon}</span>
      <span>{label}</span>
    </NavLink>
  );
}

function SidebarLinkExtern({ to, label }: { to: string; label: string }) {
  return (
    <Link
      to={to}
      className="block px-3 py-1.5 rounded-lg text-xs text-slate-500 hover:text-slate-300 hover:bg-slate-900/40 transition"
    >
      {label}
    </Link>
  );
}
